import { StudyConfig } from '@csp/csp-common-config';
import { BioSampleContainerQueryService } from '@csp/csp-common-hbs';
import { BioSampleKitCode, BioSampleKitType } from '@csp/csp-common-hbs-model';
import { ApiOptions, Maybe, OrgId, Study } from '@csp/csp-common-model';
import { ScheduleVisitTexts } from '@csp/csp-common-visits';
import { toRestOptions } from '@csp/csp-fe-auth';
import { Page } from '@csp/dmdp-api-common-dto';
import { PatientVisitService } from '../../patient-visits/service/PatientVisitService';
import { BioSampleKitItemMapper } from '../hbs-kit-details/mapper/BioSampleKitItemMapper';
import { BioSampleKitItemQueryMapper } from '../hbs-kit-details/mapper/BioSampleKitItemQueryMapper';
import { BioSampleKitItem } from '../hbs-kit-details/model/BioSampleKitItem';
import { BioSampleItemQueryService } from './BioSampleItemQueryService';

const fetchScheduleVisitTextsByKitType = async (
  { kitType, orgIds, studyConfig }: { kitType: BioSampleKitType; orgIds: OrgId[]; studyConfig: StudyConfig },
  apiOptions: ApiOptions,
): Promise<ScheduleVisitTexts[]> => {
  const visitRequestInfos = await PatientVisitService.getCachedActiveRequestInfosByOrgIds(
    orgIds,
    studyConfig,
    apiOptions,
  );

  return visitRequestInfos.getByHbsKitType(kitType).map(visitRequestInfo => visitRequestInfo.texts);
};

const fetchByKitCode = async (
  {
    kitCode,
    study,
    studyConfig,
    orgIds,
  }: {
    kitCode: BioSampleKitCode;
    study: Study;
    studyConfig: StudyConfig;
    orgIds: OrgId[];
  },
  apiOptions?: ApiOptions,
): Promise<Maybe<BioSampleKitItem>> => {
  const restOptions = toRestOptions(apiOptions);
  const [{ data: containers }, { data: sampleItems }] = await Promise.all([
    BioSampleContainerQueryService.query(
      BioSampleKitItemQueryMapper.toContainersQuery({ kitCode }),
      undefined,
      restOptions,
    ),
    BioSampleItemQueryService.query(
      {
        study,
        studyConfig,
        bioSampleQueryIn: { pageSize: Page.maxLimit, criteria: { kitCodes: [kitCode] } },
      },
      restOptions,
    ),
  ]);

  const kitDetails = BioSampleKitItemMapper.toKitDetails(containers);

  if (kitDetails) {
    const supportedVisits = await fetchScheduleVisitTextsByKitType(
      { kitType: kitDetails.kitType, orgIds, studyConfig },
      restOptions,
    );
    return BioSampleKitItemMapper.toKitItem({
      kitDetails,
      containers,
      sampleItems,
      supportedVisits,
      supportedBioSampleIdFormats: studyConfig.hbsConfig.supportedBioSampleIdFormats,
    });
  } else {
    return undefined;
  }
};

export const BioSampleKitItemQueryService = {
  fetchByKitCode,
};
